import { ElementRef, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Subject } from 'rxjs';
import * as CryptoJS from 'crypto-js';

export interface RouteSlugInfo {
  segments: string[];   // e.g. ['settings', 'portal-settings', 'interval-settings']
  leaf: string;         // e.g. 'interval-settings' (deepest slug)
  full: string;         // e.g. 'settings/portal-settings/interval-settings'
}

export interface ModuleItem {
  _id: string;
  name: string;
  moduleSlug: string;
  enabled: boolean;
  visible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  
  public allMeasurements: any[] = [];
  private selectedNodeSensors: Map<string, any[]> = new Map();
  readonly PORTAL_CONFIG: string = 'portalConfig';
  readonly INSTALLATION_CONFIG: string = 'installationData';
  readonly ENCRYPTION_KEY = '4bX9z3vQN2WcE7YpJgHdLrMfTuXsaK1!';
  readonly IV_LENGTH = 16;

  public treeManagementPreviousTab: string = 'tree-configuration';

  public isActive: boolean = false;
  public sidebarWidth: string = '';
  public contentWidth: string = '';
  public sidebarTogglerRight: string = '-1.4vw';
  public shouldHideToggleButton: boolean = false;
  public userStore: { [key: string]: any } = {};

  private chartStats: any[] = [];
  private compareStats: any[] = [];
  private chartSeries: any[] = [];
  private compareSeries: any[] = [];
  private updateChartInterval: any;
  private updateComareInterval: any;
  private updateChartFold: any;
  private updateCompareFold: any;
  private userCredentials: any = null
  public toggleTreeActive: boolean = false;

  private dataToSendInfo = new BehaviorSubject<any>(null);

  private sensorInfoSubject = new BehaviorSubject<any[]>([]);
  public sensorInfo$ = this.sensorInfoSubject.asObservable();

  private roleUpdatedSource = new Subject<void>();
  roleUpdated$ = this.roleUpdatedSource.asObservable();

  public userPermissions = new BehaviorSubject<any>([]);
  userPermissions$ = this.userPermissions.asObservable();

  public poralConfiguration$ = new BehaviorSubject<any>({});
  selectedSensorMap = new Map<string, boolean>();

  trendView = new BehaviorSubject<boolean>(false);
  trendView$ = this.trendView.asObservable();

  private currentRouteSubject = new BehaviorSubject<RouteSlugInfo>({ segments: [], leaf: '', full: '' });
  currentRoute$ = this.currentRouteSubject.asObservable();

  selectedActiveDataSensorsMap = new Set<string>();

  private readonly STORAGE_KEYS: string[] = [
    'username',
    'email',
    'permissionlevel',
    'timeZone',
    'otpAttempts',
    'treeNodeLabel',
    'groupNodeLabel',
    'masterNodeLabel',
    'roleRank',
    'roleId',
    'interval',
    'sortMode',
    'fold',
    'customerId',
    'viewMode',
    'portalTheme'
  ];

  alarmConfigSensors: Set<string> = new Set<string>();

  private getNodeFn!: (id: string) => any;
  private selectNodeFn?: (id: string) => void;
  private cancelFn?: (id: string) => void;

  public readonly excludesModuleSlugs: string[] = [
    'installation', 'role-permission', 'default-nodes',
    'portal-settings', 'developer-tool', 'portal-measurement',
    'interval-settings', 'fold-settings',
  ];
  registerCallbacks(callbacks: {
    getNode: (id: string) => any,
    selectNode?: (id: string) => void,
    deselectNode?: (id: string) => void,
    setSensor?: (id: string, sensors: any[]) => void,
    unsetSensor?: (id: string) => void,
    addMarker?: (data: any) => void,
    cancelFn?: (id: string) => void
  }) {
    this.getNodeFn = callbacks.getNode;
    this.selectNodeFn = callbacks.selectNode;
    this.cancelFn = callbacks.cancelFn;
  }

  constructor(private router: Router) {
    this.initializePortalConfig();

    this.updateStyles();

    window.addEventListener('resize', () => {
      this.updateStyles();
    });

    this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(() => {
      const routeInfo = this.getActiveRouteHierarchy();
      if (this.excludesModuleSlugs.includes(routeInfo.leaf)) {
        return;
      }

      this.currentRouteSubject.next(routeInfo);
    });
  }

  private initializePortalConfig(): void {
    this.STORAGE_KEYS.forEach((key) => {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        try {
          this.userStore[key] = JSON.parse(stored);
        } catch {
          this.userStore[key] = stored;
        }
      }
    });

    this.poralConfiguration$.next(this.userStore);
  }

  setModuleList(module: any []):void {
    this.userPermissions.next(module);
  }

  public setUserProperty(key: string, value: any): void {
    this.userStore[key] = value;
    this.poralConfiguration$.next(this.userStore);
    sessionStorage.setItem(key,value);
  }
  
  setPortalConfiguration(credentials: any) {
    this.userCredentials = credentials;
    sessionStorage.setItem(this.PORTAL_CONFIG, JSON.stringify(credentials));
  }

  getPortalConfiguration() {
    if (!this.userCredentials) {
      const storedCredentials = sessionStorage.getItem(this.PORTAL_CONFIG);
      if (storedCredentials)
        this.userCredentials = JSON.parse(storedCredentials);
    }
    return this.userCredentials;
  }

 getUserPermissions(): any[] {
    const creds = this.getPortalConfiguration();
    const permissions = creds?.permissions ?? [];
    return permissions
  }

  hasPermission(slug: string): boolean {
    const match = this.findModuleBySlug(slug, this.getUserPermissions());
    return match;
  }

  private findModuleBySlug(slug: string, modules: any[]): any {
    for (const item of modules || []) {
      if (item?.moduleSlug === slug) return item;
      if (Array.isArray(item?.subModules)) {
        const found = this.findModuleBySlug(slug, item.subModules);
        if (found) return found;
      }
    }
    return null;
  }

  hasActionPermission(moduleSlug: string, action: string): boolean {
    const permissions = this.getUserPermissions();
    const findMod = (mods: any[]): any | null => {
      for (const m of mods) {
        if (m.moduleSlug === moduleSlug) return m;
        if (m.subModules?.length) {
          const hit = findMod(m.subModules);
          if (hit) return hit;
        }
      }
      return null;
    };
    const mod = findMod(permissions);
    if (!mod || !mod.enabled) return false;
    return (mod.allowedActions as string[] | undefined)?.includes(action) ?? false;
  }


  get(key: string): any {
    if (this.userStore[key] !== undefined) {
      return this.userStore[key];
    }
    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      let parsedValue;

      try {
        parsedValue = JSON.parse(stored);
      } catch {
        parsedValue = stored;
      }
      this.userStore[key] = parsedValue;
      return parsedValue;
    }
    return null;
  }

  toggleTree() {
    this.toggleTreeActive = !this.toggleTreeActive;
  }

  toggleMap() {
    this.isActive = !this.isActive;
    this.updateStyles();
  }

  updateStyles() {
    if (window.matchMedia('(min-width: 1921px)').matches) {
      this.sidebarWidth = this.isActive ? '0%' : '21%';
      this.contentWidth = this.isActive ? '100%' : '79%';
      this.sidebarTogglerRight = '-0.8vw';
      this.shouldHideToggleButton = false;

    } else if (window.matchMedia('(min-width: 991px)').matches) {
      this.sidebarWidth = this.isActive ? '0%' : '21%';
      this.contentWidth = this.isActive ? '100%' : '79%';
      this.sidebarTogglerRight = '-1.4vw';
      this.shouldHideToggleButton = false;

    } else {
      this.sidebarWidth = '100%';
      this.contentWidth = '100%';
      this.sidebarTogglerRight = '0';
      this.shouldHideToggleButton = true;
    }
  }

  getInfoSensors(): any[] {
    return this.sensorInfoSubject.getValue();
  }

  setInfoSensors(data: any[]): void {
    this.sensorInfoSubject.next(data);
  }

  clearSensorInfo(): void {
    this.sensorInfoSubject.next([]);
  }

  setDataToSend(data: any): void {
    this.dataToSendInfo.next(data);
  }

  clearDataInfoWindow(): any {
    this.dataToSendInfo.next(null);
  }

  getDataToSend() {
    return this.dataToSendInfo.getValue();
  }

  setChartStats(stats: any[]): void {
    this.chartStats = stats;
  }

  getChartStats(): any[] {
    return this.chartStats;
  }

  setChartSeries(series: any): void {
    this.chartSeries = series;
  }

  getChartSeries(): any[] {
    return this.chartSeries;
  }

  setCompareSeries(series: any): void {
    this.compareSeries = series;
  }

  getCompareSeries(): any[] {
    return this.compareSeries;
  }

  setComparisonStats(series: any): void {
    this.compareStats = series;
  }

  getComparisonStats(): any[] {
    return this.compareStats;
  }

  setChartInterVal(interval: any): void {
    this.updateChartInterval = interval;
  }
  getChartInterVal(): any[] {
    return this.updateChartInterval;
  }

  setCompareInterVal(interval: any): void {
    this.updateComareInterval = interval;
  }
  getCompareInterVal(): any[] {
    return this.updateComareInterval;
  }

  setChartFold(fold: any): void {
    this.updateChartFold = fold;
  }

  getChartFold(): any[] {
    return this.updateChartFold;
  }

  setCompareFold(fold: any): void {
    this.updateCompareFold = fold;
  }

  getCompareFold(): any[] {
    return this.updateCompareFold;
  }

  closeModal(modal: ElementRef) {
    $(modal.nativeElement).modal('hide');
    $('.modal-backdrop').remove();
  }

  openModal(modal: ElementRef) {
    $(modal.nativeElement).modal('show');
  }

  setNodeSensors(nodeId: string, sensors: any[], moduleId?: any): void {
    if (this.selectedNodeSensors.has(nodeId)) return;
    const clonedSensors = sensors.map(s => ({
      ...s,
      __nodeId: nodeId
    }));
    this.selectedNodeSensors.set(nodeId, clonedSensors);
    this.refreshMeasurements(moduleId);
  }

  unsetNodeSensors(nodeId: string, moduleId?: any): void {
    this.selectedNodeSensors.delete(nodeId);
    this.refreshMeasurements(moduleId);
    if (this.selectedNodeSensors.size === 0) {
      this.selectedSensorMap.clear();
    }
  }

  unsetAllNodeSensors(currentModuleId?: any): void {
    this.selectedSensorMap = new Map();
    this.selectedNodeSensors.clear();
    this.allMeasurements = [];
    this.refreshMeasurements(currentModuleId);
  }

  getMeasurements(): any[] {
    return this.allMeasurements;
  }

  private refreshMeasurements(currentModuleId?: string): void {
    if (this.selectedNodeSensors.size === 0) {
      this.allMeasurements = [];
      return;
    }

    const mergedSensors: any[] = [];
    this.selectedNodeSensors.forEach(list => mergedSensors.push(...list));

    const sensorMap = new Map<string, any>();

    mergedSensors.forEach(sensor => {

      if (currentModuleId) {
        if (!sensor.visibleModules?.length) {
          return;
        }
        const hasAccess = sensor.visibleModules.some((id: any) => id?.toString() === currentModuleId?.toString());
        if (!hasAccess) {
          return;
        }
      }
      const sensorKey = sensor.id || sensor.sensorSlug;
      if (!sensorMap.has(sensorKey)) {
        sensorMap.set(sensorKey, sensor);
      }
    });
    this.allMeasurements = Array.from(sensorMap.values());
  }

  selectHierarchy(node: any, moduleId: string | null) {
    if (!node) return;
    const treeNode = this.getNodeFn(node.id || node);
    if (!treeNode) return;

    const original = treeNode.original;
    this.selectNodeFn?.(original.id);
    const isLeaf = !treeNode.children?.length;
    const hasSensors = original?.sensors?.length > 0;
    if (isLeaf && hasSensors) {
      const nodeId = original.id;
      this.setNodeSensors(nodeId, original.sensors, moduleId);
    }
    if (treeNode.children?.length > 0) {
      treeNode.children.forEach((childId: any) => {
        const child = this.getNodeFn(childId);
        this.selectHierarchy(child, moduleId);
      });
    }
  }

  deselectHierarchy(node: any, moduleId: string | null) {
    if (!node) return;
    const treeNode = this.getNodeFn(node.id || node);
    if (!treeNode) return;
    const original = treeNode.original;
    const isLeaf = !treeNode.children?.length;
    const hasSensors = original?.sensors?.length > 0;
    if (isLeaf && hasSensors) {
      const nodeId = original.id;
      this.cancelFn?.(nodeId);
      this.unsetNodeSensors(nodeId, moduleId);
    }
    if (treeNode.children?.length > 0) {
      treeNode.children.forEach((childId: any) => {
        const child = this.getNodeFn(childId);
        this.deselectHierarchy(child, moduleId);
      });
    }
  }

  setSensorSelection(sensorId: string, checked: boolean) {
    if (checked) {
      this.selectedSensorMap.set(sensorId, true);
    } else {
      this.selectedSensorMap.delete(sensorId);
    }
  }

  getSensorSelection() {
    return this.selectedSensorMap;
  }

  hasSelection(): boolean {
    return this.selectedSensorMap.size > 0;
  }


  chartResolution() {
    const fontSizes = {
      small: '9px',
      medium: '11px',
      large: '15px',
      xLarge: '0.6vw',
      xxLarge: '0.6vw',
      xxxLarge: '0.6vw',
    };

    const widths = {
      large: '60px',
      xLarge: '60px',
      xxLarge: '80px',
    };

    const screenWidth = window.innerWidth;
    
    const resolutionMap = [
      // marginLeft used to be undefined here, leaving Highcharts to auto-size the left
      // margin from each chart's own rendered y-axis labels. A chart with no data renders
      // no labels at all, so it got a ~10px margin while a populated chart next to it (with
      // the min-width:80px label formatter used by overview/data-gap) got ~100px — the two
      // charts' plot areas started at different x positions instead of lining up. Fixing it
      // to the same 100px every populated chart already lands on (verified with a headless
      // Highcharts render using the real yAxis label config) makes every chart's plot area
      // start at the same x regardless of whether it has data.
      { maxWidth: 600,      fontSize: fontSizes.small,    largeWidth: widths.large,   xTickPixelInterval: 80,  yTickPixelInterval: 40,  resetZoomFontSize: 11, resetZoomPadding: 5,  marginLeft: 100 as number | undefined, xAxisTitleOffset: 30, categoryLabelStep: 1 },
      { maxWidth: 1024,     fontSize: fontSizes.medium,   largeWidth: widths.large,   xTickPixelInterval: 100, yTickPixelInterval: 50,  resetZoomFontSize: 12, resetZoomPadding: 6,  marginLeft: 100 as number | undefined, xAxisTitleOffset: 35, categoryLabelStep: 1 },
      { maxWidth: 1920,     fontSize: fontSizes.large,    largeWidth: widths.large,   xTickPixelInterval: 120, yTickPixelInterval: 55,  resetZoomFontSize: 13, resetZoomPadding: 7,  marginLeft: 100 as number | undefined, xAxisTitleOffset: 40, categoryLabelStep: 3 },
      { maxWidth: 2560,     fontSize: fontSizes.xLarge,   largeWidth: widths.xLarge,  xTickPixelInterval: 220, yTickPixelInterval: 110, resetZoomFontSize: 22, resetZoomPadding: 12, marginLeft: 120,                             xAxisTitleOffset: 60, categoryLabelStep: 4 },
      { maxWidth: 3840,     fontSize: fontSizes.xxLarge,  largeWidth: widths.xxLarge, xTickPixelInterval: 300, yTickPixelInterval: 150, resetZoomFontSize: 30, resetZoomPadding: 16, marginLeft: 160,                             xAxisTitleOffset: 80, categoryLabelStep: 6 },
      { maxWidth: Infinity, fontSize: fontSizes.xxxLarge, largeWidth: widths.xxLarge, xTickPixelInterval: 320, yTickPixelInterval: 160, resetZoomFontSize: 32, resetZoomPadding: 18, marginLeft: 170,                             xAxisTitleOffset: 85, categoryLabelStep: 6 },
    ];

    const { fontSize, largeWidth, xTickPixelInterval, yTickPixelInterval, resetZoomFontSize, resetZoomPadding, marginLeft, xAxisTitleOffset, categoryLabelStep } = resolutionMap.find(
      (res) => screenWidth <= res.maxWidth
    )!;

    return { fontSize, largeWidth, xTickPixelInterval, yTickPixelInterval, resetZoomFontSize, resetZoomPadding, marginLeft, xAxisTitleOffset, categoryLabelStep };
  }

  globalLoadingResolution() {

    const screenWidth = window.innerWidth;

    const resolutionMap = [
      { maxWidth: 600,      titleFontSize: "16px", subTitleFontSize: "14px" },
      { maxWidth: 1024,     titleFontSize: "16px", subTitleFontSize: "14px" },
      { maxWidth: 1920,     titleFontSize: "17px", subTitleFontSize: "15px" },
      { maxWidth: 2560,     titleFontSize: "0.8vw", subTitleFontSize: "0.5vw" },
      { maxWidth: 3840,     titleFontSize: "0.8vw", subTitleFontSize: "0.5vw" },
      { maxWidth: Infinity, titleFontSize: "0.8vw", subTitleFontSize: "0.5vw"},
    ];

    const { titleFontSize, subTitleFontSize } = resolutionMap.find(
      (res) => screenWidth <= res.maxWidth
    )!;

    return { titleFontSize, subTitleFontSize };
  }

  notifyRoleChange() {
    this.roleUpdatedSource.next();
  }

  filterRolesByUser(roles: { _id: string; name: string; rank: number }[], roleRank: number) {
    const existRank = typeof roleRank === 'string' ? Number(roleRank) : roleRank;
    if (!roleRank) return roles;
    // Super role (rank 1) sees everyone. Everyone else can only manage strictly lower-ranked
    // roles — not their own rank tier, not anything above it.
    if (existRank === 1) return [...roles].sort((a, b) => a.rank - b.rank);
    return roles.filter(role => role.rank > existRank).sort((a, b) => a.rank - b.rank);
  }

  getColor(alarmType: string): string {
    if (
      alarmType === 'Lower Target Criteria' ||
      alarmType === 'Upper Target Criteria'
    ) {
      return '#fbbc05';
    } else if (
      alarmType === 'Lower Critical Limit' ||
      alarmType === 'Upper Critical Limit'
    ) {
      return '#ea4335';
    } else {
      return 'inherit';
    }
  }

  getCurrentRouteSegment(router: Router): string {
    const segments = router.url.split('/');
    return segments[segments.length - 1];
  }
  
  getBaseRoute(url: any): string {
    const path = url.split('?')[0];       // remove query params
    const segments = path.split('/');
    return segments[1] || '';
  }

  encryption(text: string): string {
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(this.ENCRYPTION_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    return (
      iv.toString(CryptoJS.enc.Hex) +
      ':' +
      encrypted.ciphertext.toString(CryptoJS.enc.Hex)
    );
  }

  private extractSegmentsFromUrl(url: string): string[] {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return cleanUrl.split('/').filter(Boolean);
  }

  getCurrentModule(routeInfo: RouteSlugInfo | string): any {
    const segments: string[] =
      typeof routeInfo === 'string'
        ? this.extractSegmentsFromUrl(routeInfo)
        : routeInfo.segments || [];

    const permissions = this.getUserPermissions?.() || [];
    const match = this.findModuleByHierarchy(segments, permissions);
    if (match) {
      return match;
    }

    return { moduleId: '', selectedNode: '', module: null, parentModule: null };
  }

  findModuleByHierarchyRecursive(segments: string[], modules: any[]): any | null {
    let specialModules: any[] = ["external", "internal"];
    let currentLevel: any[] = modules || [];
    let matchedModule: any = null;
    let parentModule: any = null;

    for (const slug of segments) {
      let found: any = currentLevel.find((mod: any) => mod?.moduleSlug === slug);

      if (!found) {
        // Not found at this level - if a special module (e.g. "internal"/
        // "external") is present here, it's a transparent hop not reflected
        // in the URL segments, so look for the slug inside its subModules too.
        const specialNode: any = currentLevel.find((mod: any) => specialModules.includes(mod?.moduleSlug));
        if (!specialNode) {
          break;
        }

        found = (specialNode.subModules || []).find((mod: any) => mod?.moduleSlug === slug);
        if (!found) {
          break;
        }

        parentModule = matchedModule || specialNode;
        matchedModule = found;
        currentLevel = found.subModules || [];
        continue;
      }

      parentModule = matchedModule || found;
      matchedModule = found;
      currentLevel = found.subModules || [];
    }

    if (!matchedModule) {
      return null;
    }

    return {
      moduleId: matchedModule.id || matchedModule._id,
      moduleSlug: matchedModule.moduleSlug,
      selectedNode: matchedModule.defaultTreeNodeId || parentModule?.defaultTreeNodeId || '',
      module: matchedModule,
      parentModule
    };
  }
  private findModuleByHierarchy(segments: string[], modules: any[]): any | null {
    let currentLevel: any[] = modules || [];
    let matchedModule: any = null;
    let parentModule: any = null;

    for (const slug of segments) {
      const found: any = currentLevel.find((mod: any) => mod?.moduleSlug === slug);
      if (!found) {
        break;
      }
      parentModule = matchedModule || found;
      matchedModule = found;
      currentLevel = found.subModules || [];
    }

    if (!matchedModule) {
      return null;
    }

    return {
      moduleId: matchedModule.id || matchedModule._id,
      moduleSlug: matchedModule.moduleSlug,
      selectedNode: matchedModule.defaultTreeNodeId || parentModule?.defaultTreeNodeId || '',
      module: matchedModule,
      parentModule
    };
  }

  /**
   * Walks a permission tree level-by-level matching each segment against
   * `moduleSlug`, like `findModuleByHierarchy`, but also reports whether the
   * *entire* requested path resolved — used by guards that must reject a
   * partially-matching path instead of silently falling back to an ancestor.
   */
  resolveModulePath(segments: string[], modules?: any[]): { module: any; fullyResolved: boolean } | null {
    let currentLevel: any[] = modules || this.getUserPermissions() || [];
    let matchedModule: any = null;

    for (const slug of segments) {
      const found: any = currentLevel.find((mod: any) => mod?.moduleSlug === slug);
      if (!found) {
        return { module: matchedModule, fullyResolved: false };
      }
      matchedModule = found;
      currentLevel = found.subModules || [];
    }

    if (!matchedModule) {
      return null;
    }

    return { module: matchedModule, fullyResolved: true };
  }

  /**
   * Recursive visibility check: a module is accessible if it's itself
   * enabled/visible, or any descendant (at any depth) is. Lets a parent
   * tab/route stay reachable when only a deeply-nested child is permitted.
   */
  /**
   * Does this module represent the given physical device type? Prefers an
   * explicit `deviceType` field (the convention new device-type modules
   * should set), but falls back to matching (or prefix-matching) its
   * `moduleSlug` — existing modules are named like 'inventia-remote-command'
   * for device type 'inventia', not an exact match.
   */
  moduleMatchesDeviceType(module: any, deviceType: string | null | undefined): boolean {
    if (!module || !deviceType) return false;
    const normalized = deviceType.toLowerCase();
    const slug = (module.moduleSlug || '').toLowerCase();
    const explicit = (module.deviceType || '').toLowerCase();
    return explicit === normalized || slug === normalized || slug.startsWith(normalized);
  }

  /**
   * Rank-sorted list of flow-settings device tabs (ecologger/inventia) the
   * user is permitted to see, under the given top-level module slug (e.g.
   * 'settings'). Single source of truth for "what tab comes first".
   */
  getFlowSettingsSubModules(parentModuleSlug: string): any[] {
    const modules = this.getUserPermissions() || [];
    const parentModule = modules.find((m: any) => m.moduleSlug === parentModuleSlug);
    const flowSettings = parentModule?.subModules?.find((m: any) => m.moduleSlug === 'flow-settings');
    return (flowSettings?.subModules || []).slice().sort((a: any, b: any) => a.rank - b.rank);
  }

  hasAccessibleDescendantOrSelf(module: any): boolean {
    if (!module) return false;
    if (module.enabled !== false && module.visible !== false) return true;
    return (module.subModules || []).some((sub: any) => this.hasAccessibleDescendantOrSelf(sub));
  }

  getMarkerIcon(productModel: any): string {
    const basePath = '../../../assets/img/';
    const defaultMarker = 'marker1.png';
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'svg'];
    let marker = productModel?.markerIcon || defaultMarker;
    const hasExtension = allowedExtensions.some(ext => marker.toLowerCase().endsWith(`.${ext}`));

    if (!hasExtension) {
      marker = `${marker}.png`;
    }

    return basePath + marker;
  }

  setAlarmConfigSensors(moduleType: string, configAlarmData: any) {

    const enabledOnly = configAlarmData.filter(
      (row: any) => row.enabled || row.isInstantEnabled
    );
    const uniqueSensorIds = enabledOnly
      .filter(
        (row: any, index: number, arr: any[]) =>
          index === arr.findIndex(
            (r: any) => r.sensor._id === row.sensor._id
          )
      )
      .map((row: any) => row.sensor._id);

    // ✅ Correct way to store in Map
    uniqueSensorIds.forEach((sensorId: any) => {
      this.alarmConfigSensors.add(sensorId);
    });
  }

  getAlarmConfigSensors(moduleType: string) {    
    return this.alarmConfigSensors;
  }

  clearConfigSensors() {
    if(this.alarmConfigSensors)
      this.alarmConfigSensors.clear();
  }

  applyRoleSort(roleList: any []): any [] {
    // Roles are always ordered by hierarchy rank — this must not follow `sortMode`, which is the
    // Manage Sorting setting for the device tree (manual vs alphabetical) and has no relation to
    // role hierarchy. Deferring to it here silently ignored `rank` (sorted roles by name instead)
    // whenever a portal had its device tree set to alphabetical sorting.
    return [...roleList].sort((a, b) => {
      const rankA = a?.rank ?? Number.MAX_SAFE_INTEGER;
      const rankB = b?.rank ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    });
  }

  setDeviceModel(deviceType: string | null): void {
    if (deviceType === null) {
      localStorage.removeItem('DEVICE_MODEL');
    } else {
      localStorage.setItem('DEVICE_MODEL', deviceType);
    }
  }

  getDeviceModel(): string | null {
    return localStorage.getItem('DEVICE_MODEL');
  }

  capitalize(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
  }

  setTrendView(value: boolean): void {
    this.trendView.next(value);
  }
  
  getActiveRouteSegment(): string {
    const segments = this.router.url.split('/').filter(Boolean);
    return segments[segments.length - 1] || '';
  }

  private getActiveRouteHierarchy(): RouteSlugInfo {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (route) {
      const slug = route.data?.['slug'] ?? route.routeConfig?.path;

      if (slug && slug !== segments[segments.length - 1]) {
        segments.push(slug);
      }

      route = route.firstChild;
    }

    return {
      segments,
      leaf: segments[segments.length - 1] ?? '',
      full: segments.join('/')
    };
  }
}
